import axios, { AxiosResponse } from 'axios'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { ICategory, ISubcategory } from 'safe'
import categories from '../reducers/categories'
import {
  CategoriesTypes,
  SET_CATEGORIES,
  CREATE_CATEGORY,
  UPDATE_CATEGORY,
  DELETE_CATEGORY,
  SubcategoriesTypes,
  CREATE_SUBCATEGORY,
  UPDATE_SUBCATEGORY,
  DELETE_SUBCATEGORY,
} from './actionTypes'

const server = process.env.REACT_APP_SERVER_URL

const setCategories = (categories: ICategory[]): CategoriesTypes => ({
  type: SET_CATEGORIES,
  payload: categories,
})
const setCategory = (category: ICategory): CategoriesTypes => ({
  type: CREATE_CATEGORY,
  payload: category,
})
const editCategory = (category: {
  id: string
  name: string
  code: string
  description: string
}): CategoriesTypes => ({
  type: UPDATE_CATEGORY,
  payload: category,
})
const deleteCategory = (id: string): CategoriesTypes => ({
  type: DELETE_CATEGORY,
  payload: id,
})

const setSubcategory = (subcategory: ISubcategory): SubcategoriesTypes => ({
  type: CREATE_SUBCATEGORY,
  payload: subcategory,
})

const editSubcategory = (subcategory: ISubcategory): SubcategoriesTypes => ({
  type: UPDATE_SUBCATEGORY,
  payload: subcategory,
})

const deleteSubcategory = (subcategory: ISubcategory): SubcategoriesTypes => ({
  type: DELETE_SUBCATEGORY,
  payload: subcategory,
})

export const fetchCategories =
  (): ThunkAction<Promise<void>, {}, {}, CategoriesTypes> =>
  async (dispatch: ThunkDispatch<{}, {}, CategoriesTypes>): Promise<void> => {
    try {
      const response: AxiosResponse<{
        status: number
        categories: ICategory[]
      }> = await axios.get(`${server}/inventory/category`)
      dispatch(setCategories(response?.data?.categories))
    } catch (e) {
      throw e
    }
  }

export const createCategory =
  (category: ICategory): ThunkAction<Promise<void>, {}, {}, CategoriesTypes> =>
  async (dispatch: ThunkDispatch<{}, {}, CategoriesTypes>): Promise<void> => {
    try {
      const response: AxiosResponse<{
        status: number
        category: ICategory,
        message: string
      }> = await axios.post(`${server}/inventory/category`, category)
      dispatch(setCategory(response.data.category))
    } catch (e) {
      throw e
    }
  }

export const updateCategory =
  (category: ICategory): ThunkAction<Promise<void>, {}, {}, CategoriesTypes> =>
  async (dispatch: ThunkDispatch<{}, {}, CategoriesTypes>): Promise<void> => {
    try {
      const response: AxiosResponse<{
        status: number
        category: {
          id: string
          name: string
          code: string
          description: string
        }
        message: string
      }> = await axios.patch(`${server}/inventory/category`, category)
      dispatch(editCategory(response.data.category))
    } catch (e) {
      throw e
    }
  }

export const removeCategory =
  (id: string): ThunkAction<Promise<void>, {}, {}, CategoriesTypes> =>
  async (dispatch: ThunkDispatch<{}, {}, CategoriesTypes>): Promise<void> => {
    try {
      await axios.delete(`${server}/inventory/category?id=${id}`)
      dispatch(deleteCategory(id))
    } catch (error) {
      throw error
    }
  }

export const createSubcategory =
  (
    subcategory: ISubcategory
  ): ThunkAction<Promise<void>, {}, {}, SubcategoriesTypes> =>
  async (
    dispatch: ThunkDispatch<{}, {}, SubcategoriesTypes>
  ): Promise<void> => {
    try {
      const response: AxiosResponse<{
        status: number
        subcategory: ISubcategory
      }> = await axios.post(`${server}/tools/subcategory`, { subcategory })
      dispatch(setSubcategory(response.data.subcategory))
    } catch (e) {
      throw e
    }
  }

export const updateSubcategory =
  (
    subcategory: ISubcategory
  ): ThunkAction<Promise<void>, {}, {}, SubcategoriesTypes> =>
  async (
    dispatch: ThunkDispatch<{}, {}, SubcategoriesTypes>
  ): Promise<void> => {
    try {
      const response: AxiosResponse<{
        status: number
        subcategory: ISubcategory
      }> = await axios.patch(`${server}/tools/subcategory`, {
        subcategory,
      })
      dispatch(editSubcategory(response.data.subcategory))
    } catch (e) {
      throw e
    }
  }

export const removeSubcategory =
  (
    subcategory: ISubcategory
  ): ThunkAction<Promise<void>, {}, {}, SubcategoriesTypes> =>
  async (
    dispatch: ThunkDispatch<{}, {}, SubcategoriesTypes>
  ): Promise<void> => {
    try {
      await axios.delete(`${server}/tools/subcategory/${subcategory.id}`)
      dispatch(deleteSubcategory(subcategory))
    } catch (error) {
      throw error
    }
  }
